"use client";

import * as React from "react";
import { Flex } from "@radix-ui/themes";
import { OrganizationDomain } from "../api/endpoint.js";
import { AlertDialog, Button } from "./elements.js";
import { Translation } from "./i18n/translation.js";

interface DeleteDomainDialogProps {
  children?: React.ReactNode;
  domain: OrganizationDomain;
  onDeleteDomain: (domainId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DeleteDomainDialog = (props: DeleteDomainDialogProps) => {
  const message = (() => {
    switch (props.domain.state) {
      case "Verified":
      case "LegacyVerified":
        return (
          <Translation
            defaultMessage="Users with the email domain {domain} will no longer be considered verified. This action is immediate and cannot be undone."
            id="HPEjJe"
            description="Warning message for removing a verified domain"
            values={{ domain: <b>{props.domain.domain}</b> }}
          />
        );
      case "Failed":
        return (
          <Translation
            defaultMessage="Are you sure you want to remove the domain {domain}? This action is immediate and cannot be undone."
            id="ig3G/s"
            description="Warning message for removing a failed domain"
            values={{ domain: <b>{props.domain.domain}</b> }}
          />
        );
      default:
        return (
          <Translation
            defaultMessage="Removing {domain} will cancel the DNS record verification process. This action is immediate and cannot be undone."
            id="zx2oPb"
            description="Warning message for removing a domain in verification"
            values={{ domain: <b>{props.domain.domain}</b> }}
          />
        );
    }
  })();

  const handleDeleteDomain = () => {
    props.onDeleteDomain(props.domain.id);
    props.onOpenChange(false);
  };

  return (
    <AlertDialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      {props.children && (
        <AlertDialog.Trigger>{props.children}</AlertDialog.Trigger>
      )}

      <AlertDialog.Content style={{ maxWidth: "488px" }}>
        <AlertDialog.Title mb="2">
          <Translation
            defaultMessage="Remove domain"
            id="0stiTP"
            description="Dialog title for removing a domain"
          />
        </AlertDialog.Title>
        <AlertDialog.Description>{message}</AlertDialog.Description>
        <Flex justify="end" gap="2" mt="5">
          <AlertDialog.Cancel>
            <Button variant="secondary">
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </AlertDialog.Cancel>
          <Button variant="destructive" onClick={handleDeleteDomain}>
            <Translation
              defaultMessage="Remove domain"
              id="OkjVyP"
              description="Confirm button text to remove domain"
            />
          </Button>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
};
